import { Component } from "@theme/component";

/** Button attributes copied onto the sticky copy so it shows the same loading and feedback states */
const MIRRORED_ATTRIBUTES = [
  "disabled",
  "data-loading",
  "data-text-feedback",
  "data-added",
  "aria-busy",
];

/**
 * @typedef {Object} StickyAddToCartButtonRefs
 * @property {HTMLButtonElement} button - The sticky copy of the add to cart button
 */

/**
 * A full-width copy of the add to cart button, pinned to the bottom of the screen on mobile.
 * It fades in while the real button is out of view and fades out when the real button scrolls back in.
 * Tapping it clicks the real button, so the add to cart behaves exactly the same.
 *
 * `data-target` points it at another button beside it instead (the Inquire block's), as a
 * selector looked up within its parent.
 *
 * @extends {Component<StickyAddToCartButtonRefs>}
 */
class StickyAddToCartButton extends Component {
  requiredRefs = ["button"];

  /** @type {HTMLButtonElement | null} */
  #target = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {boolean} */
  #targetInView = true;

  connectedCallback() {
    super.connectedCallback();

    const selector =
      this.dataset.target ?? 'add-to-cart-component [ref="addToCartButton"]';
    const target = this.parentElement?.querySelector(selector);
    if (!(target instanceof HTMLButtonElement)) return;
    this.#target = target;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#targetInView = entry.isIntersecting;
      this.#updateVisibility();
    });
    this.#intersectionObserver.observe(this.#target);

    // Variant changes morph the real button in place, so watching it keeps the label and state in sync
    this.#mutationObserver = new MutationObserver(this.#sync);
    this.#mutationObserver.observe(this.#target, {
      attributes: true,
      attributeFilter: MIRRORED_ATTRIBUTES,
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  handleClick = () => {
    this.#target?.click();
  };

  #sync = () => {
    const target = this.#target;
    if (!target) return;

    const { button } = this.refs;
    if (button.innerHTML !== target.innerHTML)
      button.innerHTML = target.innerHTML;

    for (const name of MIRRORED_ATTRIBUTES) {
      const value = target.getAttribute(name);
      if (value === null) {
        button.removeAttribute(name);
      } else if (button.getAttribute(name) !== value) {
        button.setAttribute(name, value);
      }
    }

    this.#updateVisibility();
  };

  #updateVisibility() {
    // Sold out and unavailable variants show their own in-page buttons, so the sticky copy steps aside
    const visible = !this.#targetInView && !this.#target?.disabled;
    this.dataset.visible = String(visible);
  }
}

if (!customElements.get("sticky-add-to-cart-button")) {
  customElements.define("sticky-add-to-cart-button", StickyAddToCartButton);
}
